import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TherapistModel } from '../../../models/api/TherapistModel';
import { TherapistService } from '../../../services/api/TherapistService';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { SystemUserService } from '../../../services/api/SystemUserService';
import { SystemUserModel } from '../../../models/api/SystemUserModel';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';



@Component({
  selector: 'app-therapist-add-edit',
  standalone: true,
  imports: [FormsModule, MatSlideToggleModule, MatFormFieldModule, MatTooltipModule, MatIconModule, MatInputModule, ReactiveFormsModule, MatSelectModule, MatButtonModule, MatProgressBarModule, MatSnackBarModule],

  templateUrl: './therapist-add-edit.component.html',
  styleUrl: './therapist-add-edit.component.scss'

})
export class TherapistAddEditComponent implements OnChanges {
  @Input() therapistId: number | null = null;
  @Output() formClosed = new EventEmitter<void>();
  therapistForm!: FormGroup;
  selectedFile: File | null = null;
  uploadProgress: number | null = null;
  uploadedUrl: string | null = null;
  cloudName = 'coolcodecompany';
  uploadPreset = 'ml_upload';
  uploadingFile: boolean = false;
  formSubmitted: boolean = false;
  errorMessage: string | null = null;
  isDuplicateEmail = false;

  constructor(private fb: FormBuilder, 
    private http: HttpClient, 
    private therapistService: TherapistService, 
    private systemUserService: SystemUserService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef) { }

  ngOnChanges(): void {

    if (this.therapistId) {
      this.buildEditForm();
      this.getTherapistDetails(this.therapistId);
    } else {
      this.buildCreateForm();
    }
  }

  private buildCreateForm() {
    this.therapistForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      startTime: [''],
      endTime: [''],
      therapistStatusID: 1,
      profileLargeURL: ['', [Validators.required]],
      active: [true],
      working: [true],
      acceptBookings: [true],
      password: ['', [Validators.required, Validators.minLength(5)]]
    });
  }

  private buildEditForm() {
    this.therapistForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      startTime: [''],
      endTime: [''],
      therapistStatusID: null,
      profileLargeURL: ['', [Validators.required]],
      therapistID: null,
      systemUserID: null,
      active: 1,
      working: 1,
      acceptBookings: 1,
      password: [{ value: '', disabled: true }]
    });
  }



  addTherapist() {
    this.formSubmitted = true;
    this.errorMessage = null;
    this.isDuplicateEmail = false;

    if (this.therapistForm.valid) {
      const therapist: TherapistModel = this.therapistForm.value;
      const userDetail: SystemUserModel = new SystemUserModel;

      userDetail.username = this.therapistForm.value.email;
      userDetail.password = this.therapistForm.value.password;
      userDetail.fullName = therapist.firstName + " " + therapist.lastName;
      userDetail.userTypeID = 3;
      this.systemUserService.createSystemUser(userDetail).subscribe({
        next: (response) => {

          therapist.systemUserID = response.systemUserID;
          this.therapistService.createTherapist(therapist).subscribe({
            next: (response) => {
              this.uploadingFile = false;
              this.selectedFile = null;
              this.uploadedUrl = null;
              this.uploadProgress = null;
              this.formSubmitted = false;
              this.therapistForm.reset();
              this.formClosed.emit();
              this.therapistId = null;
            },
            error: (err) => {
              console.error('Error creating therapist:', err);
            }
          })
        },
        error: (err) => {
          console.error('Error creating therapist:', err);
          if (err && err.errorcode === 409) {
            const emailControl = this.therapistForm.get('email');
            if (emailControl) {
              emailControl.setErrors({ duplicate: true });
              emailControl.markAsTouched();
              this.cdr.detectChanges();
            }
          } else {
            this.errorMessage = "An unexpected error occurred. Please try again.";
            this.cdr.detectChanges();
          }
        }
      })
    }
  }

  updateTherapist() {
    this.formSubmitted = true;
    if (this.therapistForm.valid && this.therapistId) {
      const therapist: TherapistModel = this.therapistForm.value;
      this.therapistService.updateTherapist(this.therapistId, therapist).subscribe({

        next: (response) => {
          this.uploadingFile = false;
          this.selectedFile = null;
          this.uploadedUrl = null;
          this.uploadProgress = null;
          this.therapistId = null;
          this.formSubmitted = false;

          this.snackBar.open('Therapist details updated', 'Close',{
            duration: 5000,
          });
          this.therapistForm.reset();
          this.formClosed.emit();
        },
        error: (err) => {
          console.error('Error updating therapist:', err);
        }
      })
    }
  }

  getTherapistDetails(therapistId: number) {
    if (therapistId) {
      this.therapistService.getTherapist(therapistId).subscribe(data => {
        this.populateTherapistDetails(data)
      })
    }
  }

  onFileSelected(event: any) {
    this.uploadingFile = true;
    this.selectedFile = event.target.files[0];
    this.uploadFile();
  }

  uploadFile() {
    if (!this.selectedFile) return;
    const url = `https://api.cloudinary.com/v1_1/${this.cloudName}/upload`;
    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('upload_preset', this.uploadPreset);

    this.http.post<any>(url, formData, {
      reportProgress: true,
      observe: 'events'
    }).subscribe(event => {
      if (event.type === HttpEventType.UploadProgress && event.total) {
        this.uploadProgress = Math.round(100 * event.loaded / event.total);
      } else if (event.type === HttpEventType.Response) {
        this.uploadedUrl = event.body.secure_url;
        this.uploadProgress = null;
        this.uploadingFile = false;
        this.therapistForm.get('profileLargeURL')?.setValue(this.uploadedUrl);
      }
    }, error => {
      console.error('Upload error:', error);
      this.uploadProgress = null;
      this.uploadingFile = false;
    });
  }

  populateTherapistDetails(data: any) {

    if (data.profileLargeURL) {
      this.uploadedUrl = data.profileLargeURL
    }
    this.therapistForm.patchValue({
      therapistID: data.therapistID,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      therapistStatusID: data.therapistStatusID,
      profileLargeURL: data.profileLargeURL,
      systemUserID: data.systemUserID,
      startTime: data.startTime,
      endTime: data.endTime,
      active: data.active,
      working: data.working,
      acceptBookings: data.acceptBookings
    })
  }

  cancel() {
    this.uploadingFile = false;
    this.selectedFile = null;
    this.uploadedUrl = null;
    this.uploadProgress = null;
    this.formSubmitted = false;
    this.therapistForm.reset();
    this.formClosed.emit();
    this.therapistId = null;
  }

}
